import { match, P } from 'ts-pattern';
import { ClaimType, CORSO_URL } from '~/utils/constants';
import { hasEligibleReturnResolution } from '~/utils/order';
import { makeCrewLoader, redirect } from '~/utils/routing';
import { getClaimTypeProgramPluralName } from '~/utils/theme';

type Context = Parameters<Parameters<typeof makeCrewLoader>[0]>[0]['context'];
type CrewOrder = Context['order'];
type Theme = Context['settings']['theme'];

const pickTrackers = (order: CrewOrder) =>
  order.fulfillments.map((fulfillment) => fulfillment.tracker).filter(Boolean);

const pickExistingClaims = (order: CrewOrder) => {
  const claims = order.claims?.filter((claim) => claim.linkToCustomerUi);

  return claims?.length ? claims : undefined;
};

function canProceedToWarranty(order: CrewOrder): boolean {
  // if order was created from a registration, proceed to warranty claim page
  if (order.wasCreatedFromRegistration) {
    return true;
  }

  // no valid return options exist
  const hasEligibleReturn = order.lineItems.some(hasEligibleReturnResolution);

  // no tracking exists
  const hasTracking = order.fulfillments.some(
    (fulfillment) => fulfillment.tracker,
  );

  const { wasShippingProtected } = order;

  // we can't proceed to warranty if we have a return options,
  // tracking,
  // or protection was purchased
  if (hasEligibleReturn || hasTracking || wasShippingProtected) {
    return false;
  }

  // we can proceed to warranty if we have a warranty eligible item
  return order.lineItems.some((item) => item.warranty.isEligible);
}

function getStartClaimHeadline({
  theme,
  areReturnsEnabled,
  areWarrantiesEnabled,
}: {
  theme: Theme;
  areReturnsEnabled: boolean;
  areWarrantiesEnabled: boolean;
}) {
  if (theme.orderHubStartRequestTitleText) {
    return theme.orderHubStartRequestTitleText;
  }

  const parts = [
    {
      text: getClaimTypeProgramPluralName(theme, ClaimType.return),
      condition: areReturnsEnabled,
    },
    {
      text: '&',
      condition: areReturnsEnabled && areWarrantiesEnabled,
    },
    {
      text: getClaimTypeProgramPluralName(theme, ClaimType.warranty),
      condition: areWarrantiesEnabled,
    },
  ];

  return parts.flatMap((part) => (part.condition ? part.text : [])).join(' ');
}

function getStartClaimDescription(args: {
  theme: Theme;
  areReturnsEnabled: boolean;
  areWarrantiesEnabled: boolean;
  isOrderFulfilled: boolean;
}) {
  return match(args)
    .with(
      { isOrderFulfilled: false },
      () =>
        "It doesn't look like your order has been delivered yet. Please check back later.",
    )
    .with(
      { theme: { orderHubStartRequestDetailText: P.string } },
      ({ theme }) => theme.orderHubStartRequestDetailText,
    )
    .otherwise(({ theme, areReturnsEnabled, areWarrantiesEnabled }) => {
      const parts = [
        {
          text: 'Create a request to',
          condition: true,
        },
        {
          text: 'return an item',
          condition: areReturnsEnabled,
        },
        {
          text: 'or',
          condition: areReturnsEnabled && areWarrantiesEnabled,
        },
        {
          text: `start a ${getClaimTypeProgramPluralName(theme, ClaimType.warranty).toLowerCase()} claim.`,
          condition: areWarrantiesEnabled,
        },
      ];

      return parts
        .flatMap((part) => (part.condition ? part.text : []))
        .join(' ');
    });
}

function getOrderEditDescription(theme: Theme, isOrderEditable: boolean) {
  const defaultEditableDescription =
    'Request changes to your order, such as cancellation or address updates.';
  const defaultExpiredDescription = 'Your order is no longer editable.';

  return isOrderEditable ?
      (theme.orderEditDescriptionText ?? defaultEditableDescription)
    : (theme.orderEditExpiredDescriptionText ?? defaultExpiredDescription);
}

function getPageData({ order, settings, crewClaim }: Context) {
  const {
    return: { isEnabled: areReturnsEnabled },
    warranty: { isEnabled: areWarrantiesEnabled },
  } = settings;
  const pendingClaimType =
    crewClaim?.status === 'new' && crewClaim.claimLineItems.length > 0 ?
      crewClaim.claimType
    : undefined;
  const existingClaims = pickExistingClaims(order);
  const showStartShippingProtectionClaim =
    order.wasShippingProtected || order.isUnprotectedShippingClaimPermitted;
  const showStartCrewClaim = areReturnsEnabled || areWarrantiesEnabled;

  return {
    trackers: pickTrackers(order),
    isOrderFulfilled: order.isOrderFulfilled,
    canceledOn: order.canceledOn,
    orderEdit: {
      show: settings.orderEditing.isEnabled,
      isEditable: !!order.isOrderEditable,
      description: getOrderEditDescription(
        settings.theme,
        !!order.isOrderEditable,
      ),
    },
    startClaim: {
      show:
        showStartCrewClaim ||
        !!existingClaims ||
        showStartShippingProtectionClaim,
      showStartCrewClaim,
      showStartShippingProtectionClaim,
      wasShippingProtected: order.wasShippingProtected,
      headline: getStartClaimHeadline({
        theme: settings.theme,
        areReturnsEnabled,
        areWarrantiesEnabled,
      }),
      description: getStartClaimDescription({
        theme: settings.theme,
        areReturnsEnabled,
        areWarrantiesEnabled,
        isOrderFulfilled: order.isOrderFulfilled,
      }),
      existingClaims,
      pendingClaimType,
    },
  };
}

export const { loader, useLoaderData } = makeCrewLoader(({ context }) =>
  match(context)
    .with(
      { settings: { isActive: false } },
      ({ settings: { storeUrl = CORSO_URL } }) =>
        redirect(`../inactive?redirect=${encodeURI(storeUrl)}`),
    )
    .with(
      { settings: { warranty: { isEnabled: true } } },
      ({ order }) => canProceedToWarranty(order),
      () => redirect(ClaimType.warranty.toLowerCase()),
    )
    .otherwise(getPageData),
);
